import { shallowEqual } from 'react-redux';

import { FORM_ERROR } from 'final-form';

import { DocumentRequiredFields } from '../../services/requiredDocumentFields/types';
import { Thunk } from '../../types';

import * as actions from './documentSettingsActions';

import { partition } from '../../lib/utils';

import {
    createDocumentRequiredFields,
    deleteDocumentRequiredFields,
    getDocumentRequiredFields,
    patchDocumentRequiredFields,
} from '../../services/requiredDocumentFields/api';
import { isRequiredFieldsAvailable } from './DocumentRequiredFieldsCard/helper';

export function startLoading() {
    return {
        type: actions.START_LOADING,
    } as const;
}

export function finishLoadingSuccess(requiredFields: DocumentRequiredFields[]) {
    return {
        type: actions.FINISH_LOADING_SUCCESS,
        requiredFields,
    } as const;
}

export function finishLoadingError(error: string) {
    return {
        type: actions.FINISH_LOADING_FAIL,
        error,
    } as const;
}

export type DocumentRequiredFieldsAction =
    | ReturnType<typeof startLoading>
    | ReturnType<typeof finishLoadingSuccess>
    | ReturnType<typeof finishLoadingError>;

export function fetchRequiredDocuments(): Thunk {
    return async (dispatch, getState) => {
        const {
            app: { currentUser },
        } = getState();

        if (!isRequiredFieldsAvailable(currentUser)) {
            return;
        }

        try {
            dispatch(startLoading());

            const res = await getDocumentRequiredFields(
                currentUser.currentRole.company.id,
            );

            dispatch(finishLoadingSuccess(res));
        } catch (e) {
            dispatch(finishLoadingError(e.message));
        }
    };
}

export function updateRequiredFields(
    requiredFields: DocumentRequiredFields[],
): Thunk {
    return async (dispatch, getState) => {
        const {
            documentSettings: { requiredFields: existingFields },
        } = getState();

        const existingFieldsIdsMap = new Map(
            existingFields.map(({ id, ...rest }) => [id, rest]),
        );
        const newFieldsMap = new Map(
            requiredFields.map(({ id, ...rest }) => [id, rest]),
        );

        const toDelete = existingFields.filter(
            (field) => !newFieldsMap.has(field.id),
        );
        const [sameFields, toAdd] = partition(requiredFields, ({ id }) =>
            existingFieldsIdsMap.has(id),
        );

        const toPatch = sameFields.filter((field) => {
            const oldField = existingFieldsIdsMap.get(field.id);

            return !shallowEqual(field, oldField);
        });

        try {
            await Promise.all(
                toDelete
                    .map(({ id }) => deleteDocumentRequiredFields(id))
                    .concat(
                        toAdd.map((field) =>
                            createDocumentRequiredFields(field),
                        ),
                    ),
            );
            // patch after deletions and insertions and consecutively
            for (const field of toPatch) {
                await patchDocumentRequiredFields(field);
            }
        } catch (e) {
            return {
                [FORM_ERROR]: e.message,
            };
        }

        dispatch(fetchRequiredDocuments());
        return {};
    };
}
